import { useState } from "react";

const photoLocations = [
  { name: "CVS Pharmacy — Johnson City", address: "2101 W Market St, Johnson City, TN 37604", hours: "Daily 7 AM – 10 PM" },
  { name: "Walgreens — Johnson City", address: "1805 W Market St, Johnson City, TN 37604", hours: "Daily 7 AM – 10 PM" },
  { name: "FedEx Office — Johnson City", address: "3110 Browns Mill Rd, Johnson City, TN 37604", hours: "Mon–Fri 7 AM – 9 PM, Sat–Sun 9 AM – 6 PM" },
  { name: "The UPS Store — Johnson City", address: "210 E Mountcastle Dr, Johnson City, TN 37601", hours: "Mon–Fri 8 AM – 7 PM, Sat 9 AM – 5 PM" },
  { name: "CVS Pharmacy — Elizabethton", address: "512 E Elk Ave, Elizabethton, TN 37643", hours: "Daily 7 AM – 10 PM" },
  { name: "Walgreens — Kingsport", address: "1504 Netherland Inn Rd, Kingsport, TN 37660", hours: "Daily 7 AM – 10 PM" },
  { name: "The UPS Store — Bristol", address: "1400 W State St, Bristol, TN 37620", hours: "Mon–Fri 8 AM – 6:30 PM, Sat 9 AM – 3 PM" },
  { name: "Walmart Photo Center — Johnson City", address: "2915 W Market St, Johnson City, TN 37604", hours: "Daily 8 AM – 8 PM" },
];

const dmvLocations = [
  {
    name: "Washington County Clerk — Johnson City",
    address: "100 E Main St, Johnson City, TN 37604",
    hours: "Mon–Fri 8:00 AM – 5:00 PM",
    note: "Vehicle registration, titles, and county services. Driver license services handled by TN Driver Services Center.",
    featured: true,
  },
  {
    name: "TN Driver Services — Johnson City",
    address: "4717 Lake Park Dr, Johnson City, TN 37615",
    hours: "Mon–Fri 8:00 AM – 4:30 PM, Sat 8:00 AM – 12:00 PM",
    note: "Driver's licenses, REAL ID, ID cards. Appointment recommended — online booking available at tn.gov/safety. Saturday hours available.",
    featured: false,
  },
];

export default function JohnsonCityHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 6);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Johnson City, TN Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local Tri-Cities resources to help ETSU students, Johnson City medical professionals, and Washington County families prepare for international travel.
          </p>
        </div>

        <div className="space-y-6">
          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near Johnson City, TN</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                Your passport application requires two identical 2"×2" passport photos taken within the last 6 months. Popular passport photo locations near Johnson City and throughout the Tri-Cities area include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button
                  onClick={() => setPhotoExpanded(true)}
                  className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors"
                >
                  <i className="ri-add-circle-line text-sm"></i>
                  Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://travel.state.gov/content/travel/en/passports/how-apply/photos.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>U.S. State Dept. Official Photo Requirements
                </a>
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          {/* Vital Records */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-list-3-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Obtaining Certified Documents in Washington County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Certified copies of vital records are required for passport applications — especially for first-time applicants, name changes, and minors. Here's where Johnson City residents can obtain them:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {[
                  { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Tennessee Department of Health Vital Records Office. Online, mail, or in-person options available for Washington County residents.", url: "https://www.tn.gov/health/health-program-areas/vital-records/birth-certificates.html" },
                  { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Obtain marriage license records from the Washington County Clerk. Certified copies available for name change passport applications.", url: "https://www.washingtoncountytn.org" },
                  { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Certified divorce records available through the Washington County Circuit Court Clerk. Required for name restoration passport corrections.", url: "https://www.washingtoncountytn.org" },
                  { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Certified death certificates through TN Dept. of Health Vital Records. Required for estate and legal matters in Washington County.", url: "https://www.tn.gov/health/health-program-areas/vital-records/death-records.html" },
                ].map((item, i) => (
                  <a key={i} href={item.url} target="_blank" rel="noopener noreferrer nofollow" className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 flex items-start gap-3 hover:border-[#F6C344]/50 transition-colors">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#1A365D] text-base`}></i>
                    </div>
                    <div>
                      <p className="font-bold text-[#1A365D] text-xs leading-snug">{item.label}</p>
                      <p className="text-gray-500 text-xs leading-relaxed mt-1">{item.desc}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Driver's License / ID */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver's License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Washington County Driver Services</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. If you need a new or renewed Tennessee driver's license or REAL ID, these offices serve the Johnson City area:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                        <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                        <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://www.tn.gov/safety/driver-services.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>Tennessee Driver Services
                </a>
                <a href="https://www.washingtoncountytn.org" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>Washington County Government
                </a>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times can be long. Book your TN Driver Services slot online before visiting the Lake Park Dr location.
                </p>
              </div>
            </div>
          </div>

          {/* Airports */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Johnson City, TN Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Tri-Cities Regional Airport (TRI) is the primary airport for Johnson City and Washington County travelers, with connecting flights to major international hubs. Make sure your passport is in hand well before your departure.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  {
                    code: "TRI",
                    name: "Tri-Cities Regional Airport",
                    distance: "Approximately 12 miles from Johnson City",
                    address: "2525 Highway 75, Blountville, TN 37617",
                    note: "Primary airport for Johnson City, Kingsport, and Bristol. Connecting flights to Charlotte (CLT), Atlanta (ATL), and Washington Dulles (IAD) — all major international departure hubs for Tri-Cities travelers.",
                    flightUrl: "https://flytricities.com",
                    flightLabel: "TRI Airport Website",
                    featured: true,
                  },
                  {
                    code: "TYS",
                    name: "McGhee Tyson Airport — Knoxville",
                    distance: "Approximately 100 miles southwest of Johnson City",
                    address: "2055 Alcoa Hwy, Alcoa, TN 37701",
                    note: "Larger airport with direct flights to more destinations. Serves as an alternative for Johnson City travelers seeking additional international connections beyond TRI's hub network.",
                    flightUrl: "https://flyknoxville.com/",
                    flightLabel: "TYS Airport Website",
                    featured: false,
                  },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>
                      )}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i>
                      <span>{airport.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed mb-3">{airport.note}</p>
                    <a href={airport.flightUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{airport.flightLabel}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your departure from CLT, ATL, or IAD is within two weeks, standard processing won't make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}